import React, { useState } from "react";
import { Lamp, Send, Loader2, Copy, Check, Share2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Honeypot } from "@/components/Honeypot";
import { SCENES, sceneById } from "@/lib/scenes";
import { sendLantern } from "@/lib/api";
import { toast } from "sonner";

export const LanternComposer = ({ defaultScene = "hearth" }) => {
  const [form, setForm] = useState({ scene: defaultScene, sender_name: "", recipient_name: "", message: "", hp: "" });
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sender_name.trim() || !form.message.trim()) {
      toast.error("Please add your name and a short message.");
      return;
    }
    setLoading(true);
    try {
      const { code } = await sendLantern(form);
      const url = `${window.location.origin}/lantern/${code}`;
      setLink(url);
      toast.success("Your lantern is lit. Share the link below. 🕯️");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied — send it to someone you're thinking of.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — please select and copy the link manually.");
    }
  };

  const nativeShare = async () => {
    const scene = sceneById(form.scene);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "A moment of calm for you",
          text: `I lit you a lantern — a quiet moment of ${scene?.name || "calm"}.`,
          url: link,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const reset = () => {
    setLink("");
    setForm({ scene: defaultScene, sender_name: "", recipient_name: "", message: "", hp: "" });
  };

  if (link) {
    return (
      <div className="bg-bsn-navy/70 border border-bsn-gold/30 rounded-2xl p-6 sm:p-8 text-center" data-testid="lantern-share-card">
        <div className="w-14 h-14 rounded-full bg-bsn-gold/20 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-7 h-7 text-bsn-gold" />
        </div>
        <h4 className="font-serif text-2xl font-semibold text-bsn-champagne">Your lantern is lit 🕯️</h4>
        <p className="text-bsn-ice/80 mt-2 max-w-md mx-auto">Send this private link to the person you're thinking of. When they open it, they'll find your note and a calm moment made just for them.</p>
        <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 max-w-xl mx-auto">
          <input readOnly value={link} className="flex-1 bg-bsn-midnight border border-bsn-gold/30 rounded-xl px-4 py-3 text-bsn-ice/90 text-sm" data-testid="lantern-link" onFocus={(e) => e.target.select()} />
          <Button onClick={copy} className="btn-gold font-semibold h-auto px-5" data-testid="lantern-copy">
            {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy</>}
          </Button>
          <Button onClick={nativeShare} variant="outline" className="border-bsn-gold/40 text-bsn-champagne hover:bg-bsn-gold/10 h-auto px-5" data-testid="lantern-native-share">
            <Share2 className="w-4 h-4 mr-1.5" /> Share
          </Button>
        </div>
        <button onClick={reset} className="mt-6 text-sm text-bsn-gold hover:underline" data-testid="lantern-another">Light another lantern</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-bsn-navy/60 border border-bsn-gold/25 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="lantern-form">
      <Honeypot value={form.hp} onChange={(v) => set("hp", v)} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-bsn-ice/70 mb-1.5 block">Choose a calm scene</label>
          <Select value={form.scene} onValueChange={(v) => set("scene", v)}>
            <SelectTrigger className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment" data-testid="lantern-scene"><SelectValue placeholder="Pick a scene" /></SelectTrigger>
            <SelectContent>
              {SCENES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm text-bsn-ice/70 mb-1.5 block">Their name (optional)</label>
          <Input placeholder="e.g., Maya" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="lantern-recipient" />
        </div>
      </div>
      <div>
        <label className="text-sm text-bsn-ice/70 mb-1.5 block">Your name</label>
        <Input placeholder="Who it's from" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="lantern-sender" />
      </div>
      <div>
        <label className="text-sm text-bsn-ice/70 mb-1.5 block">A gentle message</label>
        <Textarea rows={4} maxLength={1000} placeholder="Tell them why you're thinking of them. Even a few words can mean everything." value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="lantern-message" />
      </div>
      <Button type="submit" disabled={loading} className="w-full btn-gold font-semibold h-12" data-testid="lantern-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lamp className="w-4 h-4 mr-2" /> Light the Lantern</>}
      </Button>
    </form>
  );
};

export default LanternComposer;
